import React, { useState, useEffect } from 'react';
import { Alert, Button, Modal, Form } from 'react-bootstrap';
import './CookieConsent.css';

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [consent, setConsent] = useState({
    essential: true, // Always required
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    // Check if user has already given consent
    const savedConsent = localStorage.getItem('cookie-consent');
    if (!savedConsent) {
      setShowBanner(true);
    } else {
      const parsedConsent = JSON.parse(savedConsent);
      setConsent(parsedConsent);
    }
  }, []);

  const saveConsent = (consentData) => {
    localStorage.setItem('cookie-consent', JSON.stringify({
      ...consentData,
      timestamp: new Date().toISOString()
    }));
    setConsent(consentData);
    setShowBanner(false);
    setShowSettings(false);

    // Initialize analytics based on consent
    if (consentData.analytics && window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted'
      });
    }
  };

  const acceptAll = () => {
    saveConsent({
      essential: true,
      analytics: true,
      marketing: true
    });
  };

  const acceptEssential = () => {
    saveConsent({
      essential: true,
      analytics: false,
      marketing: false
    });
  };

  const handleConsentChange = (type, value) => {
    setConsent(prev => ({
      ...prev,
      [type]: value
    }));
  };

  const saveCustomConsent = () => {
    saveConsent(consent);
  };

  if (!showBanner) {
    return null;
  }

  return (
    <>
      {/* Cookie Consent Banner */}
      <div className="cookie-consent-banner">
        <Alert variant="info" className="mb-0">
          <div className="cookie-consent-content">
            <div className="cookie-text">
              <strong>🍪 We use cookies</strong>
              <p className="mb-2">
                We use essential cookies to make our site work. We'd also like to set optional cookies to help us improve our website.
                By clicking "Accept All", you agree to our use of cookies as described in our{' '}
                <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
              </p>
              <p className="mb-0 small text-muted">
                <strong>Data Controller:</strong> Skjoldan Consulting (CVR: DK21417289)
              </p>
            </div>
            <div className="cookie-buttons">
              <Button 
                variant="outline-primary" 
                size="sm" 
                onClick={() => setShowSettings(true)}
                className="me-2"
              >
                Cookie Settings
              </Button>
              <Button 
                variant="outline-secondary" 
                size="sm" 
                onClick={acceptEssential}
                className="me-2"
              >
                Essential Only
              </Button>
              <Button 
                variant="primary" 
                size="sm" 
                onClick={acceptAll}
              >
                Accept All
              </Button>
            </div>
          </div>
        </Alert>
      </div>

      {/* Cookie Settings Modal */}
      <Modal show={showSettings} onHide={() => setShowSettings(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Cookie Settings</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            We use cookies to enhance your experience on AffSimple.com. You can choose which categories of cookies you'd like to allow.
          </p>

          <div className="cookie-category mb-4">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h5>Essential Cookies</h5>
              <span className="badge bg-success">Always Active</span>
            </div>
            <p className="text-muted small">
              These cookies are necessary for the website to function and cannot be switched off. They are usually only set in response to actions made by you which amount to a request for services.
            </p>
            <p className="small">
              <strong>Examples:</strong> Authentication, security, form submissions, user preferences
            </p>
          </div>

          <div className="cookie-category mb-4">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h5>Analytics Cookies</h5>
              <Form.Group className="mb-3">
                <Form.Check 
                  type="checkbox"
                  id="analytics-consent"
                  label="Analytics cookies help us understand how visitors interact with our website."
                  checked={consent.analytics}
                  onChange={(e) => handleConsentChange('analytics', e.target.checked)}
                />
                <p className="small text-muted ms-4">
                  We use Firebase Analytics to collect information about how you use our service, 
                  including page views, sign-ups, and subscription changes. This helps us improve our service. 
                  No personally identifiable information is shared with third parties.
                </p>
              </Form.Group>
            </div>
            <p className="text-muted small">
              These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously.
            </p>
            <p className="small">
              <strong>Examples:</strong> Google Analytics, page views, user behavior tracking
            </p>
          </div>

          <div className="cookie-category mb-4">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h5>Marketing Cookies</h5>
              <div className="form-check form-switch">
                <input 
                  className="form-check-input" 
                  type="checkbox" 
                  id="marketing-switch"
                  checked={consent.marketing}
                  onChange={(e) => handleConsentChange('marketing', e.target.checked)}
                />
              </div>
            </div>
            <p className="text-muted small">
              These cookies track your online activity to help advertisers deliver more relevant advertising or to limit how many times you see an ad.
            </p>
            <p className="small">
              <strong>Examples:</strong> Social media tracking, advertising pixels, remarketing
            </p>
          </div>

          <Alert variant="info">
            <small>
              <strong>Your Rights:</strong> You can change your cookie preferences at any time by clicking the "Cookie Settings" link in our footer. 
              For more information about how we process your data, please see our <a href="/privacy" target="_blank">Privacy Policy</a>.
            </small>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowSettings(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={saveCustomConsent}>
            Save Preferences
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CookieConsent;
